import React from 'react';
import { getIcon } from '@/utils/iconMapping';
import { resolveUniqueIcons } from '@/utils/uniqueEquipmentIconResolver';
import { Option } from '@/types/helicopter';
import { cn } from '@/lib/utils';
import TonedImageIcon from './TonedImageIcon';

interface OptionIconProps {
  option: Option;
  size?: number;
  showLabel?: boolean;
  className?: string;
  variant?: 'default' | 'compact' | 'large';
}

const OptionIcon = ({ 
  option, 
  size = 48, 
  showLabel = true, 
  className = '',
  variant = 'default'
}: OptionIconProps) => {
  // Three-tier icon resolution: option.icon_url (pre-fetched) -> Unique Lucide -> Fallback Lucide
  // No async hooks - icon_url comes with the option data from v_options_with_icons view
  const uniqueIconsMap = resolveUniqueIcons([option]);
  const UniqueIcon = uniqueIconsMap.get(option.id);
  const LucideIcon = getIcon(option.icon_ref);

  const variants = {
    compact: {
      container: 'flex items-center gap-2',
      iconWrapper: 'w-10 h-10 p-1.5 rounded-lg bg-card border flex items-center justify-center',
      content: 'flex-1 min-w-0',
      title: 'font-medium text-xs truncate',
      description: 'text-xs text-muted-foreground line-clamp-1',
      value: 'text-xs font-semibold',
      iconSize: 40
    },
    default: {
      container: 'flex items-start gap-3',
      iconWrapper: 'w-12 h-12 p-1.5 rounded-xl bg-gradient-to-br from-card to-muted/20 border shadow-sm flex items-center justify-center',
      content: 'flex-1 min-w-0',
      title: 'font-semibold text-sm',
      description: 'text-sm text-muted-foreground mt-1 line-clamp-2',
      value: 'text-sm font-bold mt-2',
      iconSize: 48
    },
    large: {
      container: 'flex flex-col items-center gap-3 text-center',
      iconWrapper: 'w-14 h-14 p-2 rounded-2xl bg-gradient-to-br from-card to-muted/20 border shadow-md flex items-center justify-center',
      content: 'w-full',
      title: 'font-bold text-base',
      description: 'text-sm text-muted-foreground mt-2 line-clamp-3',
      value: 'text-base font-bold mt-3',
      iconSize: 56
    }
  };

  const styles = variants[variant];

  return (
    <div className={cn(styles.container, className)}>
      <div className={cn(styles.iconWrapper, 'text-gray-600 dark:text-gray-400')}>
        {option.icon_url ? (
          <TonedImageIcon src={option.icon_url} alt={option.name} />
        ) : UniqueIcon ? (
          <UniqueIcon className="w-full h-full" />
        ) : (
          <LucideIcon className="w-full h-full" />
        )}
      </div>
      {showLabel && (
        <div className={styles.content}>
          <div className={styles.title}>{option.name}</div>
          {option.description && (
            <div className={styles.description}>{option.description}</div>
          )}
          {option.value_add_percent > 0 && (
            <div className={cn(styles.value, 'text-success')}>
              +{option.value_add_percent}% value
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OptionIcon;
